import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseCreateDailyRentalMutation, UseDailyRentalMutation } from '../../../../hooks/mutations/useDailyRentalMutation';
import { useRentalsQuery } from '../../../../hooks/queries/useRentalsQuery';
import { DailyRental } from '../../../../services/apiSLTP/planning';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getMaterialStateInfos, materialState, systemConfigKeyEnum } from '../../../../utils/enums';
import DropDown, { OptionType } from '../../../DropDown';
import ToggleLine from '../../../ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';
import CustomTextInput from '../../../CustomTextInput';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import QuantityToggleGroup from '../../../QuantityToggleGroup';

interface ManageValidationDailyRentalModalProps extends ModalProps {
    refetch: () => void;
    dailyRental: DailyRental | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailyRentalModal: React.FC<ManageValidationDailyRentalModalProps> = ({
    open,
    handleClose,
    refetch,
    dailyRental,
    activities,
    siteName,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailyRentalId] = useState(dailyRental?._id || '');
    const [designation, setDesignation] = useState(dailyRental?.designation || '');
    const [selectedRentalType, setSelectedRentalType] = useState({
        label: dailyRental?.rentalTypeId.designation || '',
        value: dailyRental?.rentalTypeId._id || '',
    });
    const [selectedActivity, setSelectedActivity] = useState({
        label: activities.find((activity) => activity.value === dailyRental?.activityId)?.label || '',
        value: dailyRental?.activityId || '',
    });
    const [selectedState, setSelectedState] = useState<{
        label: string;
        value: string;
    }>({
        label: capitalizeFirstLetter(t(getMaterialStateInfos(dailyRental?.state ?? materialState.PRESENT))),
        value: dailyRental?.state.toString() || materialState.PRESENT.toString(),
    });
    const [unitPrice, setUnitPrice] = useState(dailyRental?.unitPrice?.toString() || '');
    const [quantity, setQuantity] = useState<number>(dailyRental?.quantity ?? 1);
    const [isExposedToWeather, setIsExposedToWeather] = useState(dailyRental?.isExposedToWeather ?? false);

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.NEW))), value: materialState.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.PRESENT))), value: materialState.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateInfos(materialState.REMOVE))), value: materialState.REMOVE.toString() },
    ];

    const { data: dataRentals } = useRentalsQuery({ queries: { active: true } });

    const { mutate: createDailyRental } = UseCreateDailyRentalMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailyRental } = UseDailyRentalMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postDailyRental = async () => {
        createDailyRental({
            body: {
                activityId: selectedActivity.value,
                rentalTypeId: selectedRentalType.value,
                state: parseInt(selectedState.value),
                designation,
                unitPrice: unitPrice.length ? parseFloat(unitPrice) : undefined,
                quantity,
                isExposedToWeather,
            },
        });
    };

    const patchDailyRental = async () => {
        if (currentDailyRentalId)
            updateDailyRental({
                id: currentDailyRentalId,
                body: {
                    activityId: selectedActivity.value,
                    rentalTypeId: selectedRentalType.value,
                    state: parseInt(selectedState.value),
                    designation,
                    unitPrice: unitPrice.length ? parseFloat(unitPrice) : null,
                    quantity,
                    isExposedToWeather,
                },
            });
    };

    return (
        <ModalBody
            closeText={t('cancel')}
            title={t('rental')}
            subTitle={!!currentDailyRentalId.length ? t('update') : t('add')}
            description={siteName}
            okText={t('validate')}
            open={open}
            handleClose={handleClose}
            handleOk={() => {
                !currentDailyRentalId.length ? postDailyRental() : patchDailyRental();
            }}
            disabledOk={!selectedRentalType.value.length || !selectedActivity.value.length || !selectedState.value.length || !designation.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedRentalType}
                items={
                    dataRentals?.map((rental) => ({
                        label: rental.designation,
                        value: rental._id,
                    })) || []
                }
                handleChange={(event, value) => setSelectedRentalType({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('company')}
                required
            />
            <CustomTextInput value={designation} onChange={(value) => setDesignation(value)} label={t('name')} required />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => setSelectedState({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('state')}
                required
            />
            <ToggleLine
                checked={isExposedToWeather}
                onChange={(checked) => setIsExposedToWeather(checked)}
                title={capitalizeFirstLetter(t('exposedToWeather'))}
            />
            <QuantityToggleGroup
                value={quantity}
                onChange={(value) => setQuantity(value)}
                options={[
                    { label: '0j', value: 0 },
                    { label: '0.5j', value: 0.5, disabled: true },
                    { label: '1j', value: 1 },
                ]}
            />

            <CustomTextInput
                value={unitPrice}
                onChange={setUnitPrice}
                label={t('unitPrice')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
        </ModalBody>
    );
};

export default ManageValidationDailyRentalModal;
